// Read-only view of the two draw realms. Nothing here needs a wallet.
//
// Every function maps 1:1 onto an EXPORTED READ of raffle.gno or speed.gno.
// Render() is not read anywhere: the listers (Weeks / Windows, with WeekCount /
// WindowCount and PageLimit) say what is recorded, and the per-draw readers
// fill in each row — the same discipline the duel board was moved to.
//
// The two realms answer almost the same surface with two different nouns, week
// and window, so one factory builds both clients and the views are written
// once. Where they genuinely differ the difference is explicit and the realm's
// reason is quoted:
//
//   - raffle.DrawOf returns NINE values (sponsored is in the tuple); speed's
//     returns EIGHT and says there is no sponsored element because its pot is
//     entry money only.
//   - raffle has EntryUnitPrice, so a 10 GNOT ticket is 40 entries; speed's
//     Buy says one ticket is one unit of weight and must not acquire an entry
//     unit.
//   - raffle has FundPot/Sponsored — the duel's rake — and the claim reads for
//     an abandoned week (ClaimPerUnitOf / ClaimableOf / EarnedOfWeek). speed
//     has none of them, by design ("V1 IS SELF-CONTAINED").
//   - speed exports WindowBlocks(); the raffle's week length is one subtraction
//     of its boundary read instead (see economics() below).
import { CONFIG } from '../config.js';
import { gnoValue, gnoValues } from './gnolit.js';
import { provider } from './reads.js';
import { drawWindow, normaliseDraw, entriesPerTicket } from '../draw.js';

const q = async (pkgPath, expr) => (await provider()).evaluateExpression(pkgPath, expr);
const one = async (pkgPath, expr) => gnoValue(await q(pkgPath, expr));
const many = async (pkgPath, expr) => gnoValues(await q(pkgPath, expr));

// An address argument is a Go string literal in the expression. Addresses are
// bech32 and cannot contain a quote, but the guard is here rather than trusted:
// an unchecked interpolation into an expression the chain evaluates is the one
// injection this file could have.
const addrArg = (a) => {
  const s = String(a);
  if (!/^g1[02-9ac-hj-np-z]{38}$/.test(s)) throw new Error(`draws: not a gno.land address: ${s}`);
  return `"${s}"`;
};
const i64 = (n) => BigInt(n).toString();

// --- the two realms' shapes -------------------------------------------------
export const SHAPES = {
  raffle: {
    kind: 'raffle',
    realm: () => CONFIG.raffleRealm,
    noun: 'week', nouns: 'weeks',
    currentIndex: 'CurrentWeek',
    endHeight: 'WeekEndHeight',
    currentEndHeight: 'CurrentWeekEndHeight',
    ticketTotalOf: 'TicketTotalOfWeek',
    ticketsOfIndex: 'TicketsOfWeek',
    spendOfIndex: 'SpendOfWeek',
    count: 'WeekCount',
    indexes: 'Weeks',
  },
  speed: {
    kind: 'speed',
    realm: () => CONFIG.speedRealm,
    noun: 'window', nouns: 'windows',
    currentIndex: 'CurrentWindow',
    endHeight: 'WindowEndHeight',
    currentEndHeight: 'CurrentWindowEndHeight',
    ticketTotalOf: 'TicketTotalOfWindow',
    ticketsOfIndex: 'TicketsOfWindow',
    spendOfIndex: 'SpendOfWindow',
    count: 'WindowCount',
    indexes: 'Windows',
  },
};

export function makeClient(kind) {
  const S = SHAPES[kind];
  if (!S) throw new Error(`draws: no such game ${kind}`);
  const R = () => S.realm();

  const client = {
    kind: 'live', game: kind, shape: S,
    realm: R,

    // --- the live draw ---
    currentIndex: () => one(R(), `${S.currentIndex}()`),

    // WHERE THE COUNTDOWN COMES FROM, IN ONE CALL. CurrentWeekEndHeight /
    // CurrentWindowEndHeight is the height the LIVE period ends at, resolved on
    // chain at one height. It exists precisely so a front end does not compose
    // `${S.endHeight}(${S.currentIndex}())` out of two queries: the period can
    // turn over between them, and the answer is then the boundary of a period
    // that already ended — the countdown jumps backwards by a whole week (or
    // hour) at the one moment anybody is watching it. Do not "optimise" this
    // into two reads.
    currentEndHeight: () => one(R(), `${S.currentEndHeight}()`),
    // The boundary of any OTHER index — a past or future period, where there is
    // no turnover race because the index is fixed. Pure arithmetic on chain: a
    // negative or far-future index is not refused (endHeight(-1) is the realm's
    // launch height).
    endHeight: (i) => one(R(), `${S.endHeight}(${i64(i)})`),

    potSize: () => one(R(), 'PotSize()'),
    ticketTotal: () => one(R(), 'TicketTotal()'),
    ticketsOf: async (addr) => one(R(), `TicketsOf(${addrArg(addr)})`),

    // --- any draw ---
    ticketTotalOf: (i) => one(R(), `${S.ticketTotalOf}(${i64(i)})`),
    ticketsOfIndex: async (i, addr) => one(R(), `${S.ticketsOfIndex}(${i64(i)}, ${addrArg(addr)})`),
    spendOfIndex: async (i, addr) => one(R(), `${S.spendOfIndex}(${i64(i)}, ${addrArg(addr)})`),
    refundOpensAt: (i) => one(R(), `RefundOpensAt(${i64(i)})`),
    refundGraceBlocks: () => one(R(), 'RefundGraceBlocks()'),
    capturedRandomness: (i) => one(R(), `CapturedRandomness(${i64(i)})`),
    escrowedCut: (i) => one(R(), `EscrowedCut(${i64(i)})`),
    async drawOf(i) { return normaliseDraw(await many(R(), `DrawOf(${i64(i)})`), kind); },

    // --- the lister ---
    count: async () => Number(await one(R(), `${S.count}()`)),
    async indexes(offset, limit) {
      const w = drawWindow(offset, limit);
      return one(R(), `${S.indexes}(${w.offset}, ${w.limit})`);
    },

    // --- the house's books, for the plaque ---
    opsPot: () => one(R(), 'OpsPot()'),
    heldCuts: () => one(R(), 'HeldCuts()'),
    // AuditInvariant is the realm's own self-check. A front end showing a
    // number the realm itself calls inconsistent should say so.
    auditOk: () => one(R(), 'AuditInvariant()'),
    auditFailure: () => one(R(), 'AuditFailure()'),
  };

  if (kind === 'raffle') {
    // The arcade's money in the live week's pot, and the dead-week claim reads.
    client.sponsored = () => one(R(), 'Sponsored()');
    client.sponsoredOf = (i) => one(R(), `SponsoredOf(${i64(i)})`);
    client.claimPerUnitOf = (i) => one(R(), `ClaimPerUnitOf(${i64(i)})`);
    client.claimableOf = async (i, addr) => one(R(), `ClaimableOf(${i64(i)}, ${addrArg(addr)})`);
    client.earnedOfIndex = async (i, addr) => one(R(), `EarnedOfWeek(${i64(i)}, ${addrArg(addr)})`);
    client.earnedTotalOf = (i) => one(R(), `EarnedTotalOfWeek(${i64(i)})`);
    client.unclaimedOf = (i) => one(R(), `UnclaimedOf(${i64(i)})`);
  } else {
    // The one cadence read either realm exports.
    client.windowBlocks = () => one(R(), 'WindowBlocks()');
    client.maxStaleBlocks = () => one(R(), 'MaxStaleBlocks()');
  }

  return client;
}

// --- the economics, read from the realm where the realm exports it ----------
//
// TicketPrice and EntryUnitPrice are EXPORTED CONSTS, and qeval evaluates a
// bare exported identifier in a package's scope — proven live on onyx-1 against
// gno.land/p/nt/ownable/v0's OwnershipTransferEvent, which is the same shape
// (scripts/prove-draws.mjs). So the price is asked of the realm and never
// hardcoded while the realm is up.
//
// THE FALLBACK IS NOT A GUESS DRESSED AS A READ. When the realm is not
// deployed, CONFIG.draw supplies the numbers and `source` says 'config' so the
// page can tell the player the price it is quoting came from this repo rather
// than from the chain.
export async function economics(kind) {
  const R = kind === 'raffle' ? CONFIG.raffleRealm : CONFIG.speedRealm;
  const d = CONFIG.draw;
  const fallback = kind === 'raffle'
    ? { ticketPrice: d.raffleTicketPrice, entryUnitPrice: d.entryUnitPrice, periodBlocks: d.raffleWeekBlocks }
    : { ticketPrice: d.speedTicketPrice, entryUnitPrice: null, periodBlocks: d.speedWindowBlocks };

  const out = { ...fallback, source: 'config', error: null };
  try {
    out.ticketPrice = BigInt(await one(R, 'TicketPrice'));
    if (kind === 'raffle') {
      out.entryUnitPrice = BigInt(await one(R, 'EntryUnitPrice'));
      // raffleWeekDays and blocksPerDay are still unexported, but the week's
      // LENGTH no longer has to be hardcoded: WeekEndHeight's own doc comment
      // points at this subtraction — "the week's length is WeekEndHeight(1) -
      // WeekEndHeight(0), so one function answers every question the constant
      // would have". Two calls are safe here where they are not for the live
      // boundary: both are pure arithmetic on immutable constants, so there is
      // no height either read could disagree about.
      const [e0, e1] = await Promise.all([
        one(R, 'WeekEndHeight(0)'), one(R, 'WeekEndHeight(1)'),
      ]);
      const span = BigInt(e1) - BigInt(e0);
      if (span <= 0n) throw new Error(`draws: WeekEndHeight(1)-WeekEndHeight(0) is ${span}`);
      out.periodBlocks = span;
      out.periodSource = 'realm';
    } else {
      out.periodBlocks = BigInt(await one(R, 'WindowBlocks()'));
      out.periodSource = 'realm';
    }
    out.source = 'realm';
  } catch (e) {
    out.error = e;
    out.periodSource = 'config';
  }
  out.entriesPerTicket = entriesPerTicket(out.ticketPrice, out.entryUnitPrice);
  return out;
}
